import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/*
 * URL writes are batched. React Router's setSearchParams hands each call the
 * params from the last render, so two setters in one handler used to wipe out
 * each other. Instead every setter patches one module-level pending copy of the
 * query string and a microtask navigates ONCE with the merged result.
 *
 * `lastNav` covers the gap between our navigate() and the router re-rendering:
 * while the router still reports the old search, later setters start from the
 * search we just navigated to rather than the stale one.
 */
let pending = null;   // { params, navigate, pathname, hash, search, replace }
let lastNav = null;   // { pathname, from, to }

const normSearch = (s) => {
  const qs = new URLSearchParams(s || '').toString();
  return qs ? `?${qs}` : '';
};

function startingParams(currentSearch, pathname) {
  if (pending) return pending.params;
  if (lastNav && lastNav.pathname === pathname && normSearch(currentSearch) === lastNav.from) return new URLSearchParams(lastNav.to);
  return new URLSearchParams(currentSearch);
}

function flush() {
  const p = pending;
  pending = null;
  if (!p) return;
  // Something navigated to another page in the same tick — don't drag it back.
  if (typeof window !== 'undefined' && window.location.pathname !== p.windowPath) return;
  const to = normSearch(p.params.toString());
  const from = normSearch(p.search);
  if (to === from) return;
  lastNav = { pathname: p.pathname, from, to };
  p.navigate({ pathname: p.pathname, search: to, hash: p.hash }, { replace: p.replace });
}

/** Apply `mutate(params)` to the pending query string; navigates once per tick. */
function queuePatch(ctx, mutate) {
  const params = startingParams(ctx.search, ctx.pathname);
  mutate(params);
  if (pending) {
    pending.params = params;
    // Any setter asking to push (replace:false) wins, so Back still works for it.
    pending.replace = pending.replace && ctx.replace;
    return;
  }
  pending = { ...ctx, params, windowPath: typeof window !== 'undefined' ? window.location.pathname : '' };
  const schedule = typeof queueMicrotask === 'function' ? queueMicrotask : (fn) => Promise.resolve().then(fn);
  schedule(flush);
}

/** Router context for queuePatch, kept fresh each render. */
function useUrlContext() {
  const location = useLocation();
  const navigate = useNavigate();
  // The router has caught up with our last navigate — stop overriding it.
  if (lastNav && (location.pathname !== lastNav.pathname || normSearch(location.search) !== lastNav.from)) lastNav = null;
  const ref = useRef(null);
  ref.current = { navigate, pathname: location.pathname, hash: location.hash, search: location.search };
  return { location, ref };
}

const writeParam = (p, name, v, defaultValue) => {
  if (v == null || v === '' || v === defaultValue) p.delete(name);
  else p.set(name, String(v));
};

const readParam = (params, name, defaultValue, allowed) => {
  const raw = params.get(name);
  if (raw == null || raw === '') return defaultValue;
  if (Array.isArray(allowed) && !allowed.includes(raw)) return defaultValue;
  return raw;
};

/**
 * useUrlState(name, defaultValue, { replace = true, allowed })
 *   → [value, setValue]
 *
 * Keeps one piece of UI state (a tab, a view, a filter) in the query string so
 * it survives refresh and the Back button and can be shared as a link. Setting
 * the default value removes the param, keeping URLs clean. Other params are
 * preserved. `allowed` (array) rejects unknown values from a hand-edited URL.
 * Several setters may be called in one handler — they're merged into one navigation.
 */
export function useUrlState(name, defaultValue = '', { replace = true, allowed } = {}) {
  const { location, ref } = useUrlContext();
  const raw = new URLSearchParams(location.search).get(name);
  const value = useMemo(() => {
    if (raw == null || raw === '') return defaultValue;
    if (Array.isArray(allowed) && !allowed.includes(raw)) return defaultValue;
    return raw;
  }, [raw, defaultValue, allowed]);

  const setValue = useCallback((next) => {
    queuePatch({ ...ref.current, replace }, (p) => {
      const v = typeof next === 'function' ? next(p.get(name) ?? defaultValue) : next;
      writeParam(p, name, v, defaultValue);
    });
  }, [name, defaultValue, replace, ref]);

  return [value, setValue];
}

/**
 * useUrlStates({ key: defaultValue, … }, { replace = true }) → [values, setMany]
 *
 * Several URL params at once. `setMany({ tab: 'invoices', status: 'all' })`
 * writes every key in a single navigation; a value equal to that key's default
 * (or null / '') removes the param. Keys not in the spec are written as given.
 */
export function useUrlStates(spec, { replace = true } = {}) {
  const { location, ref } = useUrlContext();
  const specRef = useRef(spec);
  specRef.current = spec;
  const search = location.search;
  const specKey = JSON.stringify(spec);
  const values = useMemo(() => {
    const params = new URLSearchParams(search);
    const out = {};
    Object.entries(JSON.parse(specKey)).forEach(([k, d]) => { out[k] = readParam(params, k, d); });
    return out;
  }, [search, specKey]);

  const setMany = useCallback((patch) => {
    queuePatch({ ...ref.current, replace }, (p) => {
      Object.entries(patch || {}).forEach(([k, next]) => {
        const d = specRef.current && Object.prototype.hasOwnProperty.call(specRef.current, k) ? specRef.current[k] : '';
        const v = typeof next === 'function' ? next(p.get(k) ?? d) : next;
        writeParam(p, k, v, d);
      });
    });
  }, [replace, ref]);

  return [values, setMany];
}

/**
 * useTabParam(name = 'tab', defaultTab, options) → [activeTab, setActiveTab]
 * Drop-in for `useState` behind a <TabBar>:
 *   const [tab, setTab] = useTabParam('tab', 'details', { allowed: tabs.map((t) => t.id) });
 *   <TabBar tabs={tabs} active={tab} onChange={setTab} />
 * Tab switches replace the history entry by default (Back leaves the page rather than
 * stepping through tabs); pass { replace: false } to push instead.
 */
export function useTabParam(name = 'tab', defaultTab = '', options = {}) {
  return useUrlState(name, defaultTab, options);
}

export default useUrlState;
